<template lang="pug">
v-card.suus__event__action-card(min-height="300")
  v-container.pa-8
    v-row(no-gutters)
      v-col(cols="9", sm="10")
        header.mb-4
          Header(
            :text="headerText",
            :level="5",
            :customClass="headerCustomClass",
            :is-loading="loading"
          )
          TextButton(
            v-if="isAttending && !loading",
            :label="declineButtonLabel",
            color="primary",
            @click="deregister",
            plain
          )
      v-col.d-flex.justify-end(cols="3", sm="2", v-if="icon || loading")
        StatusIcon(:icon="icon", :is-loading="loading")
    v-row(no-gutters, align="start")
      v-col(cols="12", sm="7")
        v-skeleton-loader.suus__skeleton-loader(
          v-if="loading",
          :loading="true",
          :type="skeletonLoader.type"
        )

        template(v-else-if="this.cardStatus === EVENT_STATUS.EVENT_PASSED")
          EventPassedContent

        template(v-else)
          Subtitle(
            :text="guestsText",
            customClass="mb-0",
            v-if="showGuestsText"
          )
          v-dialog(:width="modalWidth", v-model="showModal")
            template(v-slot:activator="{ on, attrs }")
              TextButton.mb-4(
                v-if="showGuestsText",
                label="Ändern",
                color="primary",
                plain,
                v-bind="attrs",
                v-on="on"
              )
            GuestModal(:guest_count="ownUserGuestsCount", @close="showModal = false")

          template(
            v-if="this.cardStatus === EVENT_STATUS.DEREGISTERED && !hasAttendees"
          )
            TextBody(:text="noAttendeesDeRegisteredText")

          template(
            v-else-if="this.cardStatus === EVENT_STATUS.REGISTER && !hasAttendees"
          )
            TextBody(:text="noAttendeesText")

          div(@click="scrollToAttendeePanel")
            Subtitle(
              :text="teaserAttendeesSubtitle",
              v-if="teaserAttendeesSubtitle"
            )
            .mb-8.d-flex.align-center(v-if="teaserAttendees")
              .suus__avatar__group.d-flex
                v-avatar.suus__avatar(
                  size="40",
                  color="primary",
                  v-for="relation in teaserAttendees",
                  v-bind:key="relation.name"
                )
                  v-img(
                    v-if="relation.avatar_url",
                    :src="relation.avatar_url",
                    :alt="relation.name"
                  )
                    template(v-slot:placeholder)
                      AvatarPlaceholder
                  v-icon(v-else, dark) {{ userIcon }}
              .px-2
                TextBody(:text="teaserAttendeesText", customClass="mb-0")
                SamePageLink(
                  label="Mehr",
                  type="hyperlink",
                  target="#suus__event__attendee-list",
                  :callback="() => addActivePanel(1)",
                  v-if="nonCoetansCount > 3"
                )
            Subtitle(
              :text="teaserOtherAttendeesSubtitle",
              customClass="mb-0",
              v-if="teaserOtherAttendeesSubtitle"
            )
            Subtitle(
              :text="teaserOtherGuestsSubtitle",
              v-if="teaserOtherGuestsSubtitle"
            )
      v-col(cols="12", sm="5", :class="confettiFlagClasses")
        ConfettiFlag(:width="graphicWidth")
    v-row(no-gutters, align="end", justify-sm="end")
      v-col.px-sm-2.py-2.mt-4.mt-md-0(
        cols="12",
        :sm="5",
        :offset-sm="this.cardStatus === EVENT_STATUS.REGISTER ? 2 : 0",
        v-if="secondaryBtnLabel"
      )
        TextButton(
          :label="secondaryBtnLabel",
          @click="secondaryClick",
          color="textColor",
          block,
          v-if="!loading"
        )
      v-col.order-first.order-sm-last.py-2.mt-4.mt-md-0(cols="12", sm="5")
        Button(
          v-if="primaryBtnLabel",
          @click="primaryClick",
          block,
          :btnLabel="primaryBtnLabel",
          :is-loading="loading",
          :color="btnColor"
        )
</template>
<script lang="ts">
import { EVENT_STATUS } from "constants/constants"
import { createNamespacedHelpers } from "vuex"
const { mapState } = createNamespacedHelpers("event")
import { mdiAccountCircleOutline } from "@mdi/js"
import { mdiCheck } from "@mdi/js"
import { mdiClose } from "@mdi/js"
import ConfettiFlag from "../graphics/ConfettiFlag.vue"
import Subtitle from "../UI/Subtitle.vue"
import Header from "../UI/Header.vue"
import Button from "../UI/Button.vue"
import TextButton from "../UI/TextButton.vue"
import TextBody from "../UI/TextBody.vue"
import Hyperlink from "../UI/Hyperlink.vue"
import StatusIcon from "../UI/StatusIcon.vue"
import SamePageLink from "../UI/SamePageLink.vue"
import AvatarPlaceholder from "../UI/AvatarPlaceholder.vue"
import GuestModal from "./GuestModal.vue"
import { computed, defineComponent } from "vue"
import { Event, Rsvp } from "suus-api"
import EventPassedContent from "components/Event/event-card/EventPassedContent.vue"
import { useOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"
import { useRSVPContext } from "app/modules/events/rsvp-context/use-rsvp-context.concern"

interface CardStatus {
  headerText?: string
  primaryBtnLabel?: string
  secondaryBtnLabel?: string
  icon?: string
}

type EventCardOptions = { [key: string]: CardStatus }

export default defineComponent({
  name: "EventCard",
  components: {
    EventPassedContent,
    ConfettiFlag,
    Subtitle,
    Header,
    TextButton,
    TextBody,
    Hyperlink,
    StatusIcon,
    SamePageLink,
    AvatarPlaceholder,
    Button,
    GuestModal,
  },
  data() {
    return {
      showModal: false,
      EVENT_STATUS: EVENT_STATUS,
      userIcon: mdiAccountCircleOutline,
    }
  },
  methods: {
    deregister(): void {
      this.decline()
    },
    handleNoGuests(): void {
      this.attend(0)
      this.setEventAttendStatusDirectly(EVENT_STATUS.FINISHED_REGISTRATION)
    },
    primaryClick(): void {
      if (this.cardStatus === EVENT_STATUS.REGISTER) {
        this.attend()
      } else if (this.cardStatus === EVENT_STATUS.REGISTERED) {
        this.showModal = true
      } else if (this.cardStatus === EVENT_STATUS.DEREGISTERED) {
        //Abgemeldet --> Anmelden
        this.attend()
      } else if (this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION) {
        this.scrollToDescription()
      } else if (this.cardStatus === EVENT_STATUS.ATTEND_ONLINE_NOW) {
        window.open(this.eventCallUrl ?? undefined, "_blank")
      }
    },
    secondaryClick(): void {
      if (this.cardStatus === EVENT_STATUS.REGISTER) {
        //Abmelden
        this.setEventAttendStatusDirectly(EVENT_STATUS.DEREGISTERED)
        this.decline()
      } else if (this.cardStatus === EVENT_STATUS.ATTEND_ONLINE_NOW) {
        this.scrollToAttendeePanel()
      } else {
        this.handleNoGuests()
      }
    },
    isMobile(): boolean {
      return this.$vuetify.breakpoint.mobile
    },
    scrollToAttendeePanel(): void {
      this.$vuetify.goTo("#suus__event__attendee-list", {
        duration: 300,
        offset: 0,
        easing: "easeInOutCubic",
      })
      this.addActivePanel(1)
    },
    scrollToDescription(): void {
      this.$vuetify.goTo("#suus__event__description", {
        duration: 300,
        offset: 0,
        easing: "easeInOutCubic",
      })
      this.addActivePanel(0)
    },
    addActivePanel(panelIndex: number): void {
      this.$store.commit("event/addActivePanel", panelIndex)
    },
  },
  computed: {
    declineButtonLabel(): string {
      return this.$t("declines.decline").toString()
    },
    cardOptions(): EventCardOptions {
      return {
        register: {
          headerText: this.$i18n
            .t("components.event_card.card_options.register.headerText")
            .toString(),
          primaryBtnLabel: this.$i18n
            .t("components.event_card.card_options.register.primaryBtnLabel")
            .toString(),
          secondaryBtnLabel: this.$i18n
            .t("components.event_card.card_options.register.secondaryBtnLabel")
            .toString(),
        },
        registered: {
          headerText: this.$i18n
            .t("components.event_card.card_options.registered.headerText")
            .toString(),
          primaryBtnLabel: this.$i18n
            .t("components.event_card.card_options.registered.primaryBtnLabel")
            .toString(),
          secondaryBtnLabel: this.$i18n
            .t(
              "components.event_card.card_options.registered.secondaryBtnLabel"
            )
            .toString(),
          icon: mdiCheck,
        },
        deregistered: {
          headerText: this.$i18n
            .t("components.event_card.card_options.deregistered.headerText")
            .toString(),
          primaryBtnLabel: this.$i18n
            .t(
              "components.event_card.card_options.deregistered.primaryBtnLabel"
            )
            .toString(),
          icon: mdiClose,
        },
        finished_registration: {
          headerText: this.$i18n
            .t(
              "components.event_card.card_options.finished_registration.headerText"
            )
            .toString(),
          primaryBtnLabel: this.$i18n
            .t(
              "components.event_card.card_options.finished_registration.primaryBtnLabel"
            )
            .toString(),
          icon: mdiCheck,
        },
        attend_online_now: {
          headerText: this.$i18n
            .t(
              "components.event_card.card_options.attend_online_now.headerText"
            )
            .toString(),
          primaryBtnLabel: this.$i18n
            .t(
              "components.event_card.card_options.attend_online_now.primaryBtnLabel"
            )
            .toString(),
          secondaryBtnLabel: this.$i18n
            .t(
              "components.event_card.card_options.attend_online_now.secondaryBtnLabel"
            )
            .toString(),
          icon: mdiCheck,
        },
        event_passed: {
          headerText: this.$i18n
            .t("components.event_card.card_options.event_passed.headerText")
            .toString(),
        },
      }
    },
    modalWidth(): number {
      // TODO: Refactor into mixin, also used in EventAttendeeList
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 600
        case "sm":
          return 800
        case "md":
          return 600
        default:
          return 800
      }
    },
    eventId(): string {
      return this.$route.params.event_id
    },
    eventCallUrl(): string | null {
      return this.eventData.custom_call_url
    },
    isAttending(): boolean {
      return (
        this.cardStatus === EVENT_STATUS.REGISTERED ||
        this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION
      )
    },
    confettiFlagClasses(): string[] {
      return [
        `suus__grapic d-none d-sm-flex`,
        `${
          (this.cardStatus === EVENT_STATUS.DEREGISTERED ||
            this.cardStatus === EVENT_STATUS.EVENT_PASSED) &&
          "disabled"
        }`,
      ]
    },
    headerCustomClass(): string {
      return this.isAttending && !this.loading ? "mb-0" : "mb-9"
    },
    showOtherAttendeesInsteadOfCoetans(): boolean {
      return !!(
        this.nonCoetans &&
        this.nonCoetansCount &&
        ((this.coetanenCount <= 0 && !this.ownUserAttends) ||
          (this.coetanenCount === 1 && this.ownUserAttends))
      )
    },
    noAttendeesText(): string | null {
      return !this.hasAttendees
        ? this.$t("components.event_card.no_attendees_text").toString()
        : null
    },
    noAttendeesDeRegisteredText(): string {
      return this.$t("components.event_card.deregistered_text", [
        this.ownUserName.length > 0 ? `, ${this.ownUserName}` : "",
      ]).toString()
    },
    teaserAttendees(): Rsvp[] | null {
      if (
        !this.showOtherAttendeesInsteadOfCoetans &&
        this.coetanAttendees &&
        this.coetanAttendees.length
      ) {
        return this.coetanAttendees.slice(0, 3)
      }
      if (this.nonCoetans && this.nonCoetansCount) {
        return this.nonCoetans.slice(0, 3)
      }
      return null
    },
    teaserAttendeesSubtitle(): string | null {
      if (this.ownUserIsFirstAttendee) {
        const user = this.ownUserName.length > 0 ? `, ${this.ownUserName}` : ""
        return this.$t(
          "components.event_card.attendees_subtitle.user_is_first",
          [user]
        ).toString()
      }
      if (this.ownUserAttends && !this.showOtherAttendeesInsteadOfCoetans) {
        return this.coetanenCount <= 2
          ? this.$t(
              "components.event_card.attendees_subtitle.one_coetan_attends"
            ).toString()
          : this.$t(
              "components.event_card.attendees_subtitle.more_coetans_attend",
              [this.coetanenCount - 1]
            ).toString()
      }
      if (!this.showOtherAttendeesInsteadOfCoetans) {
        return this.coetanenCount === 1
          ? this.$t(
              "components.event_card.attendees_subtitle.one_coetan_attends"
            ).toString()
          : this.$t(
              "components.event_card.attendees_subtitle.more_coetans_attend",
              [this.coetanenCount]
            ).toString()
      }
      if (this.nonCoetansCount > 0) {
        return this.nonCoetansCount === 1
          ? this.$t(
              "components.event_card.attendees_subtitle.one_club_member_attends"
            ).toString()
          : this.$t(
              "components.event_card.attendees_subtitle.more_club_members_attends",
              [this.nonCoetansCount]
            ).toString()
      }
      return null
    },
    teaserAttendeesText(): string {
      if (!this.teaserAttendees) return ""
      let names = this.teaserAttendees.map((el) => {
        return el.name
      })
      let dots = names.length < 3 ? "" : "... "
      if (names.length === 1) {
        return names.join("")
      }
      if (names.length === 2) {
        return names.join(" und ")
      }
      if (this.$vuetify.breakpoint.xs) {
        return names.join(", ").substr(0, 35) + dots
      }
      if (this.$vuetify.breakpoint.md || this.$vuetify.breakpoint.lg) {
        return names.join(", ").substr(0, 66) + dots
      }
      return names.join(", ") + dots
    },
    teaserOtherAttendeesSubtitle(): string | null {
      if (
        !this.showOtherAttendeesInsteadOfCoetans &&
        this.nonCoetansCount > 0
      ) {
        return this.$t("components.event_card.other_attendees_subtitle", [
          this.nonCoetansCount,
        ]).toString()
      }
      return null
    },
    teaserOtherGuestsSubtitle(): string | null {
      if (this.totalGuestsCount > 0) {
        return this.totalGuestsCount === 1
          ? this.$t(
              "components.event_card.other_guests_subtitle.one"
            ).toString()
          : this.$t("components.event_card.other_guests_subtitle.more", [
              this.totalGuestsCount,
            ]).toString()
      }
      return null
    },
    headerText(): string | null {
      if (this.cardOptions[this.cardStatus].headerText) {
        return this.cardOptions[this.cardStatus].headerText || null
      }
      return null
    },
    primaryBtnLabel(): string | null {
      if (this.cardOptions[this.cardStatus].primaryBtnLabel) {
        return this.cardOptions[this.cardStatus].primaryBtnLabel || null
      }
      return null
    },
    secondaryBtnLabel(): string | null {
      if (this.cardOptions[this.cardStatus].secondaryBtnLabel) {
        return this.cardOptions[this.cardStatus].secondaryBtnLabel || null
      }
      return null
    },
    btnColor(): string {
      return this.cardStatus === EVENT_STATUS.ATTEND_ONLINE_NOW
        ? "highlight"
        : "primary"
    },
    showGuestsText(): boolean {
      return !!(
        this.eventData &&
        this.eventData.allow_guests &&
        this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION
      )
    },
    guestsText(): string | null {
      if (this.showGuestsText) {
        if (this.ownUserGuestsCount > 0) {
          return this.$t("components.event_card.guests_text.your_guests", [
            this.ownUserGuestsCount,
          ]).toString()
        }
        return this.$t("components.event_card.guests_text.no_guests").toString()
      }
      return null
    },
    icon(): string | null {
      if (this.cardOptions[this.cardStatus].icon) {
        return this.cardOptions[this.cardStatus].icon || null
      }
      return null
    },
    graphicWidth(): string {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "180"
        case "sm":
          return "100%"
        default:
          return "250"
      }
    },
    skeletonLoader(): object {
      return {
        type: "sentences, list-item-avatar-three-line, list-item",
      }
    },
    ...mapState({
      eventData: (state: any) => state.currentEvent.data as Event,
      eventLoading: (state: any) => state.currentEvent.loading as boolean,
      allow_guests: (state: any) =>
        (state.currentEvent.data &&
          state.currentEvent.data.allow_guests) as boolean,
    }),
    totalGuestsCount(): number {
      return this.guestsCount
    },
    hasAttendees(): boolean {
      return this.attendsCount > 0
    },
    loading(): boolean {
      return (
        this.$store.getters["event/loading"] ||
        this.ownUserStatusLoading ||
        this.loading
      )
    },
  },
  setup() {
    const {
      eventAttendStatus,
      setEventAttendStatusDirectly,
      ownUserStatusLoading,
      ownUserGuestsCount,
      ownUserAttends,
      ownUserName,
      ownUserId,
      updateAttendance,
    } = useOwnUserAttendanceContext()
    const {
      loading,
      updateRSVP,
      countExpectedByReply,
      countByReplyAndSection,
      byReplyAndSection,
      guestsCount,
    } = useRSVPContext()

    const coetans = byReplyAndSection("attend", "coetans")
    const coetansCount = countByReplyAndSection("attend", "coetans")
    const nonCoetans = byReplyAndSection("attend", "nonCoetans")
    const nonCoetansCount = countByReplyAndSection("attend", "nonCoetans")
    const attendsCount = countExpectedByReply("attend")

    const attend = async (guests_count?: number) => {
      await updateAttendance(() =>
        updateRSVP(parseInt(ownUserId.value), "attend", guests_count)
      )
    }

    const decline = async () => {
      await updateAttendance(() =>
        updateRSVP(parseInt(ownUserId.value), "decline")
      )
    }

    const ownUserIsFirstAttendee = computed(() => {
      return (
        ownUserAttends.value &&
        coetans.value.length > 0 &&
        coetans.value[0].personal_record_id.toString() === ownUserId.value
      )
    })

    return {
      cardStatus: eventAttendStatus,
      attend,
      decline,
      setEventAttendStatusDirectly,
      ownUserStatusLoading,
      ownUserGuestsCount,
      ownUserAttends,
      ownUserName,
      ownUserIsFirstAttendee,
      nonCoetansCount,
      nonCoetans,
      coetanAttendees: coetans,
      coetanenCount: coetansCount,
      loading: loading,
      guestsCount,
      attendsCount,
    }
  },
})
</script>

<style lang="sass">
.fade-enter-active, .fade-leave-active
  transition: all .5s ease-out
.fade-enter, .fade-leave-to
  opacity: 0
  transform: scale(1.1)
.suus__event__action-card
  .suus__avatar__group
    i
      box-shadow: 0 0 1px 0 white inset, 0 0 1px 0 white
    .suus__avatar
      width: 60px
    .suus__avatar:nth-child(2),
    .suus__avatar:nth-child(3)
      margin-left: -20px !important
    .suus__grapic
      &.disabled
        opacity: 0.3
    .suus__skeleton-loader
      .v-skeleton-loader__bone
        padding-left: 0 !important
</style>
